import { useForm } from '@mantine/form';
import { Stack, TextInput, Textarea } from '@mantine/core';
import { useClientTerminology } from '@/views/list/NetworkTerms';
import Modal from '@/components/Modals/Modal';

export interface NewListForm {
  name: string;
  description: string;
}

interface NewListFormProps {
  opened: boolean;
  onClose: () => void;
  onSubmit: (results: NewListForm) => void;
  title: string;
  submitButtonText: string;
  initialValues?: NewListForm;
  isSaving?: boolean;
}

export default function ListDetailsForm({
  opened,
  onClose,
  onSubmit,
  title,
  submitButtonText,
  initialValues = { name: '', description: '' },
  isSaving = false,
}: NewListFormProps) {
  const intl = useClientTerminology();
  const form = useForm({
    initialValues,
    validate: {
      name: (value) =>
        value.trim().length === 0
          ? intl.formatMessage({
              id: 'customList.nameRequired',
              defaultMessage: 'List name is required',
            })
          : null,
    },
  });

  const handleFormSubmit = form.onSubmit((values) => {
    onSubmit(values);
  });

  const handleFormCancel = () => {
    form.reset();
    onClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={handleFormCancel}
      title={title}
      primaryButtonTestId="submit-custom-list-button"
      isLoading={isSaving}
      secondaryLabel={intl.formatMessage({
        id: 'action.cancel',
      })}
      primaryLabel={submitButtonText}
      onSecondaryAction={handleFormCancel}
      formToSubmit="custom-list-form"
    >
      <form
        id="custom-list-form"
        onSubmit={handleFormSubmit}
        autoComplete="off"
      >
        <Stack gap="md">
          <TextInput
            label={intl.formatMessage({
              id: 'customList.name',
              defaultMessage: 'List name',
            })}
            placeholder={intl.formatMessage({
              id: 'customList.namePlaceholder',
              defaultMessage: 'List name',
            })}
            {...form.getInputProps('name')}
            required
            data-autofocus
            data-testid="custom-list-name-input"
          />
          <Textarea
            label={intl.formatMessage({
              id: 'customList.description',
              defaultMessage: 'List description (optional)',
            })}
            placeholder={intl.formatMessage({
              id: 'customList.descriptionPlaceholder',
              defaultMessage: 'Description',
            })}
            {...form.getInputProps('description')}
            autosize
            minRows={2}
            data-testid="custom-list-description-input"
          />
        </Stack>
      </form>
    </Modal>
  );
}
